import {
  Box,
  Flex,
  FormControl,
  HStack,
  Icon,
  Input,
  useOutsideClick,
  VStack,
} from '@chakra-ui/react';
import React, { useCallback, useMemo } from 'react';
import { useEffect, useState } from 'react';
import { OwnBookingsBookingsData } from '../../../types/AdminTypes';
import { BsHouseDoor, HiOutlineSelector } from 'react-icons/all';

interface SelectObjectProps {
  handleObjectSelected: (object: OwnBookingsBookingsData) => void;
  objectSelected: OwnBookingsBookingsData;
  ownBookingsData: Array<OwnBookingsBookingsData>;
}

export const SelectObject = (props: SelectObjectProps): JSX.Element => {
  const { handleObjectSelected, objectSelected, ownBookingsData } = props;
  const [searchInput, setSearchInput] = useState<string>('');
  const [openSelect, setOpenSelect] = useState<boolean>(false);
  const [openFilteredList, setOpenFilteredList] = useState<boolean>(false);

  const filteredObjects = useMemo(() => {
    return openFilteredList
      ? ownBookingsData.filter(object => object.Name.includes(searchInput))
      : ownBookingsData;
  }, [ownBookingsData, searchInput, openFilteredList]);
  const ref = React.useRef();

  useOutsideClick({
    // @ts-ignore
    ref: ref,
    handler: () => {
      setOpenSelect(false);
      setOpenFilteredList(false);
    },
  });

  useEffect(() => {
    setSearchInput(objectSelected.Name);
  }, [objectSelected]);

  const handleInput = useCallback(
    e => {
      setSearchInput(e.target.value);
      setOpenFilteredList(true);
    },
    [searchInput],
  );

  const handleClick = useCallback(() => {
    setOpenSelect(true);
    setOpenFilteredList(false);
  }, [openSelect, setOpenSelect, setOpenFilteredList]);

  const handleToggle = useCallback(() => {
    setOpenSelect(!openSelect);
    setOpenFilteredList(false);
  }, [openSelect, setOpenSelect, setOpenFilteredList]);

  return (
    <>
      <Box
        // @ts-ignore
        ref={ref}
        flex={1}
        bgColor={'white'}
        borderRadius={4}
        width={'100%'}
        position={'relative'}
        zIndex={'101'}>
        <FormControl>
          <HStack alignItems={'stretch'} position={'relative'}>
            <Input
              onClick={handleClick}
              _focus={{ border: 'none' }}
              placeholder="Bitte wählen Sie ein Objekt aus der Liste"
              size="xs"
              h={'68px'}
              pl={{ base: '40px', md: '53px' }}
              pr={'40px'}
              fontWeight={'medium'}
              variant={'outline'}
              value={searchInput}
              onChange={handleInput}
              fontSize={'100%'}
              textOverflow="ellipsis"
              whiteSpace={'nowrap'}
              overflow={'hidden'}
            />
            <HStack
              alignItems={'center'}
              justifyContent={'center'}
              position={'absolute'}
              left={{ base: 0, md: '15px' }}
              height={'68px'}>
              <Icon as={BsHouseDoor} color="gray.500" h={6} w={6} />
            </HStack>
            <HStack
              alignItems={'center'}
              position={'absolute'}
              right={0}
              w={'40px'}
              height={'68px'}
              cursor={'pointer'}
              display={'flex'}
              justify={'flexStart'}
              zIndex={2}>
              <Icon
                onClick={handleToggle}
                as={HiOutlineSelector}
                color="blue.500"
                h={6}
                w={6}
                _hover={{ bg: 'blue.50' }}
              />
            </HStack>
          </HStack>
        </FormControl>
        {openSelect && (
          <VStack
            flex={1}
            width={'100%'}
            position={'absolute'}
            bgColor={'white'}
            pt={'20px'}
            zIndex={999}
            w={'100%'}
            maxH={500}
            overflowX={'hidden'}
            overflowY={'auto'}
            left={0}
            mb={'20px'}
            top={'100px'}
            css={{
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                width: '6px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: '#bfbfbf',
                borderRadius: '24px',
                height: '50px',
              },
            }}
            borderColor={'gray.200'}
            borderRadius={'0 0 8 8'}
            boxShadow={'0 3px 10px rgb(0 0 0 / 0.2)'}
            onClick={e => e.stopPropagation()}>
            {filteredObjects.length ? (
              filteredObjects.map((object, i) => (
                <Flex
                  key={i}
                  p={4}
                  w={'100%'}
                  _hover={{ boxShadow: 'lg', bgColor: 'blue.100' }}
                  height={'auto'}
                  mt={'8px'}
                  alignItems={'center'}
                  flexDirection={{ base: 'column', xl: 'row' }}
                  cursor={'pointer'}
                  onClick={e => {
                    handleObjectSelected(object);
                    handleToggle();
                  }}>
                  {object.Name}
                </Flex>
              ))
            ) : (
              <Flex
                borderRadius={'8'}
                w={'100%'}
                height={'auto'}
                mt={'8px'}
                p={'8px 16px'}
                alignItems={'center'}
                flexDirection={{ base: 'column', xl: 'row' }}
                cursor={'pointer'}>
                Es wurde kein Objekt gefunden.
              </Flex>
            )}
          </VStack>
        )}
      </Box>
    </>
  );
};
